if (!customElements.get("quote-request-modal")) {
  const QUOTE_REQUEST_TIMEOUT_MS = 30000;
  const MESSAGE_MAX_LENGTH = 2000;

  function getStrings(modal) {
    const dataset = modal?.dataset || {};
    const generic = dataset.errorGeneric || "We couldn’t send your quote request. Please try again.";
    return {
      nameRequired: dataset.errorNameRequired || "Please enter your name.",
      emailRequired: dataset.errorEmailRequired || "Please enter your email address.",
      emailInvalid: dataset.errorEmailInvalid || "Please enter a valid email address.",
      quantityInvalid: dataset.errorQuantityInvalid || "Please enter a quantity of at least 1.",
      lineQuantityInvalid:
        dataset.errorLineQuantityInvalid || "Enter a quantity of at least 1 for each product.",
      emptyList: dataset.errorEmptyList || "Add at least one product to your quote before submitting.",
      maxItems: dataset.errorMaxItems || "You can request a quote for up to 20 products at a time.",
      variantMissing:
        dataset.errorVariantMissing ||
        "We couldn’t load product details for this quote. Please refresh the page and try again.",
      genericError: generic,
      shopifyApiFailed:
        dataset.errorShopifyApiFailed ||
        "We couldn’t save your quote request right now. Please try again in a moment, or contact us if this keeps happening.",
      rateLimit: dataset.errorRateLimit || "Please wait a moment and try again.",
      honeypotError: generic,
      lineQuantityLabel: dataset.lineQuantityLabel || "Quantity",
      lineQuantityAriaLabel: dataset.lineQuantityAriaLabel || "Quantity for {{ product }}",
      lineQuantityDecreaseLabel: dataset.lineQuantityDecreaseLabel || "Decrease quantity for {{ product }}",
      lineQuantityIncreaseLabel: dataset.lineQuantityIncreaseLabel || "Increase quantity for {{ product }}",
      removeItemLabel: dataset.removeItemLabel || "Remove {{ product }} from quote",
      removeItemText: dataset.removeItemText || "Remove",
      itemsCountOne: dataset.itemsCountOne || "1 item",
      itemsCountOther: dataset.itemsCountOther || "{{ count }} items",
      skuLabel: dataset.skuLabel || "SKU",
    };
  }

  function generateReference() {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const datePart = [
      now.getFullYear(),
      pad(now.getMonth() + 1),
      pad(now.getDate()),
    ].join("");
    const timePart = [pad(now.getHours()), pad(now.getMinutes()), pad(now.getSeconds())].join("");
    const randomPart = Math.random().toString(36).slice(2, 8).toUpperCase();
    return `SURGE-QR-${datePart}-${timePart}-${randomPart}`;
  }

  function variantGid(numericId) {
    if (!numericId) return "";
    const id = String(numericId).replace(/\D/g, "");
    return id ? `gid://shopify/ProductVariant/${id}` : "";
  }

  function normalizeVariantId(id) {
    if (!id) return "";
    return String(id).replace(/\D/g, "");
  }

  function prefersReducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
  }

  function formatLabel(template, productTitle) {
    return String(template || "").replace(/\{\{\s*product\s*\}\}/gi, productTitle || "product");
  }

  function lineFromVariant(variant, productHandle) {
    if (!variant) return null;
    const imageSrc =
      variant.featured_image?.src || variant.featured_media?.preview_image?.src || variant.image?.src || "";
    return {
      variantId: String(variant.id || ""),
      productSku: variant.sku || "",
      productImageUrl: imageSrc,
      productHandle: productHandle || "",
    };
  }

  class QuoteRequestModal extends ModalDialog {
    constructor() {
      super();
      this.endpoint = this.dataset.quoteEndpoint || "";
      this.strings = getStrings(this);
      this.isSubmitting = false;
      this.lastSubmitPayload = null;
      this.globalVariantUnsubscriber = null;
      this.activeProductHandle = null;
      this.variantCache = {};

      this.formPanel = this.querySelector('[data-quote-panel="form"]');
      this.successPanel = this.querySelector('[data-quote-panel="success"]');
      this.form = this.querySelector("[data-quote-form]");
      this.formError = this.querySelector("[data-quote-form-error]");
      this.formErrorText = this.querySelector("[data-quote-form-error-text]");
      this.formErrorActions = this.querySelector("[data-quote-form-error-actions]");
      this.retryButton = this.querySelector("[data-quote-retry]");
      this.submitButton = this.querySelector("[data-quote-submit]");
      this.submitLabel = this.querySelector("[data-quote-submit-label]");
      this.submitLoading = this.querySelector("[data-quote-submit-loading]");
      this.successReferenceEl = this.querySelector("[data-quote-success-reference]");
      this.copyClip = this.querySelector("copy-to-clipboard");
      this.linesContainer = this.querySelector("[data-quote-lines]");
      this.linesCount = this.querySelector("[data-quote-lines-count]");
      this.linesEmpty = this.querySelector("[data-quote-lines-empty]");
      this.linesError = this.querySelector("[data-quote-lines-error]");
      this.lineTemplate = this.querySelector("[data-quote-line-template]");
      this.honeypot = this.querySelector("[data-quote-honeypot]");

      this._onLineQtyChange = this.onLineQtyChange.bind(this);
      this._onLineQtyStep = this.onLineQtyStep.bind(this);
      this._onLineRemove = this.onLineRemove.bind(this);

      this.querySelector("[data-quote-close-success]")?.addEventListener("click", () => this.hide());
      this.form?.addEventListener("submit", this.onSubmit.bind(this));
      this.retryButton?.addEventListener("click", this.onRetry.bind(this));
    }

    connectedCallback() {
      super.connectedCallback();

      if (!this._backdropClickBound) {
        this._backdropClickBound = true;
        this.addEventListener("click", (event) => {
          if (event.target.classList.contains("modal-dialog-wrap")) {
            this.hide();
          }
        });
      }

      this.bindGlobalVariantChange();

      if (this._delegationBound) return;
      this._delegationBound = true;
      document.addEventListener("click", this.onDocumentClick.bind(this));
    }

    disconnectedCallback() {
      this.globalVariantUnsubscriber?.();
      this.globalVariantUnsubscriber = null;
    }

    get storage() {
      return window.QuoteRequestStorage;
    }

    bindGlobalVariantChange() {
      if (this.globalVariantUnsubscriber || typeof subscribe !== "function") return;
      if (typeof PUB_SUB_EVENTS === "undefined") return;

      this.globalVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.productVariantChange, (data) => {
        const variant = data?.variant;
        if (!variant?.id) return;

        if (this.activeProductHandle) {
          this.variantCache[this.activeProductHandle] = variant;
        }

        if (this.hasAttribute("open") && this.activeProductHandle) {
          this.syncActiveProductLineFromVariant(variant, this.activeProductHandle);
        }
      });
    }

    onDocumentClick(event) {
      const trigger = event.target.closest("[data-quote-open]");
      if (!trigger) return;
      event.preventDefault();
      this.openFromTrigger(trigger);
    }

    resolveLineFromTrigger(trigger) {
      const dataset = trigger.dataset;
      const productHandle = dataset.productHandle || "";
      let variantId = dataset.variantId || "";

      const productForm = trigger.closest("product-form");
      const formVariantId = productForm?.querySelector('[name="id"]')?.value;
      if (formVariantId) {
        variantId = formVariantId;
      }

      const cached = this.variantCache[productHandle];
      const cachedLine = lineFromVariant(cached, productHandle);

      const line = {
        variantId: String(variantId || cachedLine?.variantId || ""),
        productTitle: dataset.productTitle || "",
        productHandle,
        productUrl: dataset.productUrl || "",
        productImageUrl: dataset.productImageUrl || cachedLine?.productImageUrl || "",
        productSku: dataset.productSku || cachedLine?.productSku || "",
        quantity: 1,
      };

      if (cachedLine?.variantId && normalizeVariantId(line.variantId) === normalizeVariantId(cachedLine.variantId)) {
        if (cachedLine.productImageUrl) line.productImageUrl = cachedLine.productImageUrl;
        if (cachedLine.productSku) line.productSku = cachedLine.productSku;
      }

      return line;
    }

    openFromTrigger(trigger) {
      if (!this.storage) {
        if (typeof console !== "undefined" && console.error) {
          console.error("[quote-request] QuoteRequestStorage is not available");
        }
        return;
      }

      const line = this.resolveLineFromTrigger(trigger);
      const result = this.storage.addOrUpdateFromLine(line);

      if (!result.ok) {
        if (result.reason === "max_items") {
          this.setLinesError(this.strings.maxItems);
        }
        this.renderLineItems();
        this.showFormPanel();
        this.clearErrors();
        this.show(trigger);
        return;
      }

      this.activeProductHandle = line.productHandle || null;
      this.reference = generateReference();
      this.renderLineItems();
      this.showFormPanel();
      this.clearErrors();
      this.show(trigger);
    }

    syncActiveProductLineFromVariant(variant, productHandle) {
      if (!this.storage || !variant?.id) return;

      const patch = lineFromVariant(variant, productHandle);
      if (!patch?.variantId) return;

      const items = this.storage.getItems();
      const handle = productHandle || this.activeProductHandle;
      const lineIndex = items.findIndex((item) => item.productHandle === handle);

      if (lineIndex < 0) {
        const byVariant = items.findIndex(
          (item) => normalizeVariantId(item.variantId) === normalizeVariantId(patch.variantId),
        );
        if (byVariant < 0) return;
        items[byVariant] = { ...items[byVariant], ...patch, quantity: items[byVariant].quantity };
      } else {
        const existing = items[lineIndex];
        items[lineIndex] = {
          ...existing,
          variantId: patch.variantId,
          productSku: patch.productSku || existing.productSku,
          productImageUrl: patch.productImageUrl || existing.productImageUrl,
          quantity: existing.quantity,
        };
      }

      this.storage.setItems(items);
      this.renderLineItems();
    }

    renderLineItems() {
      if (!this.linesContainer || !this.lineTemplate || !this.storage) return;

      const items = this.storage.getItems();
      const fragment = document.createDocumentFragment();
      this.updateLineCount(items.length);

      items.forEach((item) => {
        const row = this.lineTemplate.content.firstElementChild.cloneNode(true);
        const variantId = item.variantId;
        row.dataset.variantId = variantId;

        const titleEl = row.querySelector("[data-quote-line-title]");
        const skuRow = row.querySelector("[data-quote-line-sku-row]");
        const skuLabel = row.querySelector("[data-quote-line-sku-label]");
        const skuEl = row.querySelector("[data-quote-line-sku]");
        const imageSlot = row.querySelector("[data-quote-line-image-slot]");
        const image = row.querySelector("[data-quote-line-image]");
        const qtyInput = row.querySelector("[data-quote-line-qty]");
        const qtyValue = row.querySelector("[data-quote-line-qty-value]");
        const qtyLabel = row.querySelector("[data-quote-line-qty-label]");
        const decreaseBtn = row.querySelector("[data-quote-line-qty-decrease]");
        const increaseBtn = row.querySelector("[data-quote-line-qty-increase]");
        const removeBtn = row.querySelector("[data-quote-line-remove]");
        const qtyError = row.querySelector("[data-quote-line-qty-error]");

        if (titleEl) titleEl.textContent = item.productTitle;
        if (skuLabel) skuLabel.textContent = `${this.strings.skuLabel}:`;
        if (skuEl) skuEl.textContent = item.productSku || "—";
        if (skuRow) {
          if (item.productSku) {
            skuRow.removeAttribute("hidden");
          } else {
            skuRow.setAttribute("hidden", "");
          }
        }

        if (image && item.productImageUrl) {
          image.src = item.productImageUrl;
          image.alt = item.productTitle;
          image.removeAttribute("hidden");
          imageSlot?.classList.add("has-image");
        } else {
          image?.setAttribute("hidden", "");
          imageSlot?.classList.remove("has-image");
        }

        if (qtyInput) {
          qtyInput.value = String(item.quantity);
          qtyInput.dataset.variantId = variantId;
          qtyInput.id = `QuoteRequest-qty-${variantId}`;
        }

        if (qtyValue) {
          qtyValue.textContent = String(item.quantity);
        }

        if (qtyLabel && qtyInput) {
          qtyLabel.textContent = this.strings.lineQuantityLabel;
          qtyLabel.setAttribute("for", qtyInput.id);
        }

        if (decreaseBtn) {
          decreaseBtn.dataset.variantId = variantId;
          decreaseBtn.dataset.step = "-1";
          decreaseBtn.setAttribute(
            "aria-label",
            formatLabel(this.strings.lineQuantityDecreaseLabel, item.productTitle),
          );
          decreaseBtn.disabled = parseInt(item.quantity, 10) <= 1;
          decreaseBtn.addEventListener("click", this._onLineQtyStep);
        }

        if (increaseBtn) {
          increaseBtn.dataset.variantId = variantId;
          increaseBtn.dataset.step = "1";
          increaseBtn.setAttribute(
            "aria-label",
            formatLabel(this.strings.lineQuantityIncreaseLabel, item.productTitle),
          );
          increaseBtn.addEventListener("click", this._onLineQtyStep);
        }

        if (removeBtn) {
          removeBtn.textContent = this.strings.removeItemText;
          removeBtn.setAttribute("aria-label", formatLabel(this.strings.removeItemLabel, item.productTitle));
          removeBtn.dataset.variantId = variantId;
          removeBtn.addEventListener("click", this._onLineRemove);
        }

        if (qtyError) {
          qtyError.dataset.variantId = variantId;
        }

        fragment.appendChild(row);
      });

      this.linesContainer.replaceChildren(fragment);

      if (this.linesEmpty) {
        if (items.length > 0) {
          this.linesEmpty.setAttribute("hidden", "");
        } else {
          this.linesEmpty.removeAttribute("hidden");
        }
      }
    }

    updateLineCount(count) {
      if (!this.linesCount) return;

      if (count <= 0) {
        this.linesCount.setAttribute("hidden", "");
        this.linesCount.textContent = "";
        return;
      }

      const template = count === 1 ? this.strings.itemsCountOne : this.strings.itemsCountOther;
      this.linesCount.textContent = String(template).replace(/\{\{\s*count\s*\}\}/gi, count);
      this.linesCount.removeAttribute("hidden");
    }

    onLineQtyChange(event) {
      const input = event.target;
      const variantId = input?.dataset?.variantId;
      if (!variantId || !this.storage) return;

      const qty = parseInt(input.value, 10);
      if (Number.isNaN(qty) || qty < 1) return;

      this.storage.updateQuantity(variantId, qty);
      this.updateStepperState(input, qty);
      this.clearLineQtyError(variantId);
      this.clearLinesError();
    }

    onLineQtyStep(event) {
      const button = event.currentTarget;
      const variantId = button?.dataset?.variantId;
      const row = button?.closest("[data-quote-line]");
      const input = row?.querySelector("[data-quote-line-qty]");
      if (!variantId || !input || !this.storage) return;

      const current = parseInt(input.value, 10);
      const step = parseInt(button.dataset.step, 10);
      const next = Math.max(1, (Number.isNaN(current) ? 1 : current) + (Number.isNaN(step) ? 0 : step));

      input.value = String(next);
      const valueEl = row?.querySelector("[data-quote-line-qty-value]");
      if (valueEl) valueEl.textContent = String(next);
      this.storage.updateQuantity(variantId, next);
      this.updateStepperState(input, next);
      this.clearLineQtyError(variantId);
      this.clearLinesError();
    }

    updateStepperState(input, quantity) {
      const row = input?.closest("[data-quote-line]");
      const decreaseBtn = row?.querySelector("[data-quote-line-qty-decrease]");
      if (decreaseBtn) {
        decreaseBtn.disabled = quantity <= 1;
      }
    }

    onLineRemove(event) {
      const variantId = event.currentTarget?.dataset?.variantId;
      if (!variantId || !this.storage) return;
      this.storage.removeItem(variantId);
      this.renderLineItems();
      this.clearLinesError();
    }

    syncQuantitiesFromDom() {
      if (!this.storage || !this.linesContainer) return;
      this.linesContainer.querySelectorAll("[data-quote-line-qty]").forEach((input) => {
        const variantId = input.dataset.variantId;
        const qty = parseInt(input.value, 10);
        if (variantId && !Number.isNaN(qty) && qty >= 1) {
          this.storage.updateQuantity(variantId, qty);
        }
      });
    }

    showFormPanel() {
      this.formPanel?.removeAttribute("hidden");
      this.successPanel?.setAttribute("hidden", "");
    }

    syncCopyReference(reference) {
      if (this.successReferenceEl) {
        this.successReferenceEl.textContent = reference;
      }
      if (this.copyClip) {
        this.copyClip.originalText = reference;
      }
      const copyBtn = this.querySelector("[data-quote-copy-reference]");
      if (copyBtn) {
        const labelBase = copyBtn.dataset.copyLabelBase || "Copy";
        copyBtn.setAttribute("aria-label", `${labelBase} ${reference}`);
      }
    }

    showSuccessPanel(reference) {
      this.formPanel?.setAttribute("hidden", "");
      this.successPanel?.removeAttribute("hidden");
      this.syncCopyReference(reference);
      this.hideRetry();
      this.lastSubmitPayload = null;
    }

    hide() {
      super.hide();
      this.activeProductHandle = null;
    }

    hideRetry() {
      this.formErrorActions?.setAttribute("hidden", "");
      this.lastSubmitPayload = null;
    }

    clearErrors() {
      this.formError?.setAttribute("hidden", "");
      if (this.formErrorText) this.formErrorText.textContent = "";
      this.hideRetry();
      this.clearLinesError();
      this.querySelectorAll("[data-quote-field-error]").forEach((el) => {
        el.setAttribute("hidden", "");
        el.textContent = "";
      });
      this.querySelectorAll("[data-quote-line-qty-error]").forEach((el) => {
        el.setAttribute("hidden", "");
        el.textContent = "";
      });
      this.querySelectorAll(".field-input[aria-invalid]").forEach((input) => {
        input.removeAttribute("aria-invalid");
        input.classList.remove("is-invalid", "is-invalid-flash");
      });
    }

    setLinesError(message) {
      if (!this.linesError) return;
      this.linesError.textContent = message;
      this.linesError.removeAttribute("hidden");
    }

    clearLinesError() {
      this.linesError?.setAttribute("hidden", "");
      if (this.linesError) this.linesError.textContent = "";
    }

    setLineQtyError(variantId, message) {
      const errorEl = this.linesContainer?.querySelector(
        `[data-quote-line-qty-error][data-variant-id="${variantId}"]`,
      );
      const input = this.linesContainer?.querySelector(`[data-quote-line-qty][data-variant-id="${variantId}"]`);
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.removeAttribute("hidden");
      }
      if (input) {
        input.setAttribute("aria-invalid", "true");
        input.classList.add("is-invalid");
        if (!this._firstInvalidField) {
          this._firstInvalidField = input;
        }
      }
    }

    clearLineQtyError(variantId) {
      const errorEl = this.linesContainer?.querySelector(
        `[data-quote-line-qty-error][data-variant-id="${variantId}"]`,
      );
      const input = this.linesContainer?.querySelector(`[data-quote-line-qty][data-variant-id="${variantId}"]`);
      errorEl?.setAttribute("hidden", "");
      if (errorEl) errorEl.textContent = "";
      input?.removeAttribute("aria-invalid");
      input?.classList.remove("is-invalid");
    }

    setFieldError(fieldName, message) {
      const errorEl = this.querySelector(`[data-quote-field-error="${fieldName}"]`);
      const input = this.querySelector(`[data-quote-field="${fieldName}"]`);
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.removeAttribute("hidden");
      }
      if (input) {
        input.setAttribute("aria-invalid", "true");
        input.classList.add("is-invalid");
        if (!prefersReducedMotion()) {
          input.classList.remove("is-invalid-flash");
          void input.offsetWidth;
          input.classList.add("is-invalid-flash");
        }
        if (!this._firstInvalidField) {
          this._firstInvalidField = input;
        }
      }
    }

    focusFirstInvalidField() {
      if (!this._firstInvalidField) return;
      this._firstInvalidField.focus({ preventScroll: true });
      this._firstInvalidField.scrollIntoView({ block: "nearest", behavior: prefersReducedMotion() ? "auto" : "smooth" });
      this._firstInvalidField = null;
    }

    normalizeServerError(message) {
      if (typeof message !== "string") return this.strings.genericError;
      const normalized = message.trim().toLowerCase();
      if (normalized === "shopify api request failed") {
        return this.strings.shopifyApiFailed;
      }
      return message;
    }

    setFormError(message, { showRetry = false } = {}) {
      if (this.formError && this.formErrorText) {
        this.formErrorText.textContent = message;
        this.formError.removeAttribute("hidden");
      }
      if (showRetry && this.formErrorActions) {
        this.formErrorActions.removeAttribute("hidden");
      } else {
        this.hideRetry();
      }
    }

    setSubmitting(submitting) {
      this.isSubmitting = submitting;
      this.form?.setAttribute("aria-busy", submitting ? "true" : "false");
      this.form?.classList.toggle("is-submitting", submitting);
      if (!this.submitButton) return;
      this.submitButton.disabled = submitting;
      this.submitButton.classList.toggle("is-loading", submitting);
      if (this.submitLabel) this.submitLabel.hidden = submitting;
      if (this.submitLoading) this.submitLoading.hidden = !submitting;
    }

    getFormValues() {
      const get = (name) => this.querySelector(`[data-quote-field="${name}"]`)?.value?.trim() || "";
      return {
        customerName: get("customerName"),
        customerEmail: get("customerEmail"),
        phone: get("phone"),
        message: get("message"),
        website: this.honeypot?.value?.trim() || "",
      };
    }

    validateClient(values) {
      this._firstInvalidField = null;
      let valid = true;
      const strings = this.strings;

      this.syncQuantitiesFromDom();
      const items = this.storage?.getItems() || [];

      if (items.length === 0) {
        this.setLinesError(strings.emptyList);
        valid = false;
      }

      if (!values.customerName) {
        this.setFieldError("customerName", strings.nameRequired);
        valid = false;
      }

      if (!values.customerEmail) {
        this.setFieldError("customerEmail", strings.emailRequired);
        valid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.customerEmail)) {
        this.setFieldError("customerEmail", strings.emailInvalid);
        valid = false;
      }

      items.forEach((item) => {
        const qty = parseInt(item.quantity, 10);
        if (Number.isNaN(qty) || qty < 1) {
          this.setLineQtyError(item.variantId, strings.lineQuantityInvalid);
          valid = false;
        }

        const gid = variantGid(item.variantId);
        if (!gid) {
          this.setFormError(strings.variantMissing);
          if (typeof console !== "undefined" && console.error) {
            console.error("[quote-request] Missing variantId for line", item);
          }
          valid = false;
        }
      });

      if (values.website) {
        this.setFormError(strings.honeypotError);
        valid = false;
      }

      if (!valid) {
        this.focusFirstInvalidField();
        return null;
      }

      return { ...values, lineItems: items };
    }

    buildPayload(values) {
      const message =
        values.message.length > MESSAGE_MAX_LENGTH
          ? values.message.slice(0, MESSAGE_MAX_LENGTH)
          : values.message;

      const lineItems = values.lineItems.map((item) => {
        const line = {
          variantId: variantGid(item.variantId),
          quantity: parseInt(item.quantity, 10),
        };
        if (item.productTitle) line.productTitle = item.productTitle;
        if (item.productHandle) line.productHandle = item.productHandle;
        if (item.productUrl) line.productUrl = item.productUrl;
        if (item.productImageUrl) line.productImageUrl = item.productImageUrl;
        if (item.productSku) line.productSku = item.productSku;
        return line;
      });

      const payload = {
        reference: this.reference,
        customerName: values.customerName,
        customerEmail: values.customerEmail,
        lineItems,
      };
      if (values.phone) payload.phone = values.phone;
      if (message) payload.message = message;

      return payload;
    }

    applyServerDetails(details) {
      if (!Array.isArray(details)) return;
      const fieldMap = {
        customerName: "customerName",
        name: "customerName",
        customerEmail: "customerEmail",
        email: "customerEmail",
      };

      details.forEach((detail) => {
        const field = detail?.field || detail?.path;
        const message = detail?.message || detail?.error;
        if (!field || !message) return;
        const mapped = fieldMap[field];
        if (mapped) {
          this.setFieldError(mapped, message);
        } else if (String(field).startsWith("lineItems")) {
          this.setLinesError(message);
        } else {
          this.setFormError(message);
        }
      });
    }

    async parseResponseBody(response) {
      const text = await response.text();
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch {
        return null;
      }
    }

    isRetryableFailure(response, error) {
      if (error) return true;
      if (!response) return true;
      return response.status >= 500;
    }

    resetFormAfterSuccess() {
      this.form?.reset();
      const nameInput = this.querySelector('[data-quote-field="customerName"]');
      const emailInput = this.querySelector('[data-quote-field="customerEmail"]');
      const phoneInput = this.querySelector('[data-quote-field="phone"]');
      if (nameInput && nameInput.defaultValue) nameInput.value = nameInput.defaultValue;
      if (emailInput && emailInput.defaultValue) emailInput.value = emailInput.defaultValue;
      if (phoneInput && phoneInput.defaultValue) phoneInput.value = phoneInput.defaultValue;
      if (this.honeypot) this.honeypot.value = "";
      this.storage?.clear();
      this.renderLineItems();
    }

    async submitPayload(payload) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), QUOTE_REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch(this.endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        const body = await this.parseResponseBody(response);

        if (response.ok && body?.ok) {
          this.showSuccessPanel(body.reference || payload.reference || this.reference);
          this.resetFormAfterSuccess();
          return;
        }

        if (response.status === 429) {
          this.lastSubmitPayload = null;
          this.setFormError(this.strings.rateLimit);
          return;
        }

        const serverError = this.normalizeServerError(
          body?.error && typeof body.error === "string" && !body.error.toLowerCase().includes("invalid submission")
            ? body.error
            : this.strings.genericError,
        );

        if (body?.details) {
          this.applyServerDetails(body.details);
        }

        if (
          response.status === 400 &&
          body?.error &&
          String(body.error).toLowerCase().includes("invalid submission")
        ) {
          this.lastSubmitPayload = null;
          this.setFormError(this.strings.honeypotError);
        } else if (
          !this.querySelector("[data-quote-field-error]:not([hidden])") &&
          !this.linesContainer?.querySelector("[data-quote-line-qty-error]:not([hidden])")
        ) {
          const retryable = this.isRetryableFailure(response);
          this.setFormError(serverError, { showRetry: retryable });
          this.lastSubmitPayload = retryable ? payload : null;
        } else {
          this.lastSubmitPayload = null;
        }
      } catch (error) {
        clearTimeout(timeoutId);
        this.setFormError(this.strings.genericError, { showRetry: true });
        this.lastSubmitPayload = payload;
        if (typeof console !== "undefined" && console.error) {
          console.error("[quote-request] Submit failed", error);
        }
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;

      this.reference = generateReference();
      this.clearErrors();
      const values = this.getFormValues();
      const validated = this.validateClient(values);
      if (!validated) return;

      const payload = this.buildPayload(validated);
      this.setSubmitting(true);

      try {
        await this.submitPayload(payload);
      } finally {
        this.setSubmitting(false);
      }
    }

    async onRetry() {
      if (this.isSubmitting || !this.lastSubmitPayload) return;
      this.clearErrors();
      this.setSubmitting(true);
      try {
        await this.submitPayload(this.lastSubmitPayload);
      } finally {
        this.setSubmitting(false);
      }
    }
  }

  customElements.define("quote-request-modal", QuoteRequestModal);
}
